import { useEffect } from "react";

import { _CollectionType } from "apollo/graphql.generated";
import { useGetCategoriesQuery } from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/queries.graphql.generated";
import useEnableCategoriesAndTagsInput from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/useEnableCategoriesAndTagsInput";

const useCategories = ({
  selectedCategories,
  onChangeCategories,
  collectionType,
  isCurated,
  chainId,
}: {
  selectedCategories?: string[] | null;
  onChangeCategories: (categories: string[]) => void;
  collectionType: _CollectionType;
  isCurated: boolean;
  chainId: string;
}) => {
  const enableCategories = useEnableCategoriesAndTagsInput(chainId);
  const { data, loading } = useGetCategoriesQuery({
    skip: !enableCategories,
  });

  useEffect(() => {
    if (!enableCategories) {
      return onChangeCategories([]);
    }

    const categories = data?.getCategories || [];
    const predefinedCategories: string[] = [];

    // Collection type predefined category
    const collectionTypeCategory = categories.find(
      (category) =>
        category.slug === collectionType.toLowerCase() &&
        category.collectionTypes.includes(collectionType)
    );
    if (collectionTypeCategory) {
      predefinedCategories.push(collectionTypeCategory.slug);
    }

    // Curated outputs predefined category
    if (isCurated) {
      const curatedCategory = categories.find(
        (category) =>
          category.slug === "curated" &&
          category.collectionTypes.includes(collectionType)
      );
      if (curatedCategory) {
        predefinedCategories.push(curatedCategory.slug);
      }
    }

    const userSelectableCategories = selectedCategories
      ?.map((slug) => categories.find((category) => category.slug === slug))
      .filter((category) => category != null && category.selectable);

    const userSelectableSlugs =
      userSelectableCategories?.map((category) => category!.slug) || [];

    onChangeCategories(addSlugs(predefinedCategories, userSelectableSlugs));
  }, [data?.getCategories, collectionType, isCurated, enableCategories]);

  const categories = data?.getCategories || [];

  const selectableCategories = categories.filter(
    (category) =>
      category.selectable && category.collectionTypes.includes(collectionType)
  );

  const userCategories = selectedCategories
    ?.map((slug) => categories.find((category) => category.slug === slug))
    .filter((category) => category != null);

  return {
    categories: selectableCategories,
    userCategories,
    enableCategories,
    loading,
  };
};

const addSlugs = (slugs: string[], selectedCategories?: string[] | null) => {
  return [
    ...new Set([...slugs, ...(selectedCategories ? selectedCategories : [])]),
  ];
};

export default useCategories;
